/**
 * The strip over the draft: the caption ("3 of 4 found · 1 request removed"), one count
 * badge per group in group order ("1 to pick", "1 to confirm", "2 matched", "1 no item"),
 * the quantity offer, and the "Show details" switch. On a laptop the switch puts the long
 * figures in the cells (`data-long-figures` on its wrapper); on a phone and in Outlook it
 * opens every card.
 *
 * The quantity offer (ease round, 13 Oct 2026): "Set quantity for all lines" opens one
 * dialog for many lines. Since 14 Oct 2026 (product owner) a line the mail gave no
 * quantity for has the quantity 1 with the mark "Quantity not given, 1 used" in its cell,
 * so the strip offers no "Set 1 for all" any more.
 */

import { Icon } from "@norican/icons";
import { Badge, Button, Switch, Typography } from "@norican/react";

import { stripBadges, type StripCounts } from "@/features/case-review/draft-line-groups";
import { STATUS_ICON, STATUS_TONE } from "@/lib/line-icons";
import { ACTION_LABELS, LINE_STATUS_WORDS } from "@/lib/sales-flow";

/** The quantity offer of the strip: shown on an open case with two or more lines to quote. */
export interface StripQuantityOffer {
  /** Opens "Set quantity for all lines". */
  onSetAll: () => void;
}

export interface DraftStripProps {
  caption: string;
  counts: StripCounts;
  /** True when there are rows: the badges and the switch are shown. */
  hasRows: boolean;
  detailsOn: boolean;
  onDetailsChange: (on: boolean) => void;
  /** The switch means long figures in the cells (the table), not every card open. */
  longFigures: boolean;
  /** The quantity offer; null or absent on a closed case and with fewer than two lines to quote. */
  quantity?: StripQuantityOffer | null;
}

export function DraftStrip({
  caption,
  counts,
  hasRows,
  detailsOn,
  onDetailsChange,
  longFigures,
  quantity = null,
}: DraftStripProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-draft-strip>
      <Typography as="span" variant="caption" tone="subtle" className="tabular-nums">
        {caption}
      </Typography>
      {hasRows && (
        <span className="flex flex-wrap items-center gap-1" data-draft-counts>
          {stripBadges(counts).map(({ kind, word, text }) => (
            <Badge
              key={kind}
              tone={STATUS_TONE[word] ?? "neutral"}
              variant={word === LINE_STATUS_WORDS.noItem ? "outline" : "subtle"}
              size="sm"
            >
              <span className="inline-flex items-center gap-1 tabular-nums">
                <Icon icon={STATUS_ICON[word]} size="xs" />
                {text}
              </span>
            </Badge>
          ))}
        </span>
      )}
      {hasRows && quantity && (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1" data-quantity-offer>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            onClick={quantity.onSetAll}
            data-set-quantity-all
          >
            {ACTION_LABELS.setQuantityAll}
          </Button>
        </span>
      )}
      {hasRows && (
        <span className="ml-auto" data-long-figures={longFigures && detailsOn ? "" : undefined}>
          <Switch.Root
            checked={detailsOn}
            onCheckedChange={(checked) => onDetailsChange(checked)}
            label={<span className="text-fg-muted text-xs">{ACTION_LABELS.showDetails}</span>}
          />
        </span>
      )}
    </div>
  );
}
